import React from "react";
import { Link } from "react-router-dom";
import { useTable, Column, useSortBy, CellProps } from "react-table";
import moment from "moment";
import { DateCalendar } from "@mui/x-date-pickers";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { DumpEvent, DumpEventFilters, ExtractionStatus } from "../../../models";
import {
  getDumpEvents,
  getDumpEventsSummary,
  updateDumpEvent,
  selectDumpEvents,
  selectGetDumpEventsResult,
  selectDumpEventsSummary,
  selectGetDumpEventsSummaryResult,
} from "../dumpEventsSlice";
import { RangeTotalBar } from "../../Receipts/RangeTotalBar/RangeTotalBar";
import { getDriversV2 } from "../../DriversV2/driversV2Slice";
import { getDumpYardTemplates } from "../dumpYardsSlice";
import {
  confidenceBand,
  dumpsterSizeLabel,
  formatMoney,
  formatWeight,
  receiptTypeLabel,
  statusPillClass,
} from "./receiptDisplay";
import { ReceiptModal } from "../ReceiptModal/ReceiptModal";
import { EditDumpEventModal } from "../EditDumpEventModal/EditDumpEventModal";
import { ReassignOrderModal } from "../ReassignOrderModal/ReassignOrderModal";
import { PendingOveragesCard } from "../PendingOveragesCard/PendingOveragesCard";
import { DumpReceiptOverageModal } from "../DumpReceiptOverageModal/DumpReceiptOverageModal";

import "./DumpEventReceiptsTable.scss";

const PAGE_SIZE = 25;

interface Props {
  // Optional hook for callers that want to react to a receipt being opened;
  // the table opens its own ReceiptModal regardless.
  onView?: (id: string) => void;
}

export function DumpEventReceiptsTable({ onView }: Props) {
  const dispatch = useAppDispatch();
  const dumpEvents = useAppSelector(selectDumpEvents);
  const getDumpEventsResult = useAppSelector(selectGetDumpEventsResult);
  const summary = useAppSelector(selectDumpEventsSummary);
  const summaryResult = useAppSelector(selectGetDumpEventsSummaryResult);
  const drivers = useAppSelector((s) => s.driversV2.drivers);
  const dumpYardTemplates = useAppSelector((s) => s.dumpYards.templates);

  const [offset, setOffset] = React.useState(0);
  // "day" is the default — the existing single-day navigator.  "range" shows
  // From/To date inputs and the RangeTotalBar above the table.
  const [mode, setMode] = React.useState<"day" | "range">("day");
  // Receipts are browsed one day at a time via the date navigator (like
  // /schedules), defaulting to today. The day drives startDate/endDate so the
  // existing server-side date filter is unchanged.
  const [dayMoment, setDayMoment] = React.useState(() =>
    moment().startOf("day")
  );
  // Range-mode bounds (default = today).
  const [rangeStart, setRangeStart] = React.useState(() =>
    moment().startOf("day")
  );
  const [rangeEnd, setRangeEnd] = React.useState(() =>
    moment().endOf("day")
  );
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const datePickerRef = React.useRef<HTMLDivElement>(null);
  const [filters, setFilters] = React.useState<DumpEventFilters>({
    startDate: moment().startOf("day").toDate(),
    endDate: moment().endOf("day").toDate(),
  });
  const [confBand, setConfBand] = React.useState<"all" | "high" | "med" | "low">(
    "all"
  );
  const [selectedReceiptId, setSelectedReceiptId] = React.useState<
    string | null
  >(null);
  const [selectedEditId, setSelectedEditId] = React.useState<string | null>(
    null
  );
  // The dump receipt whose "Invoice" (weight-overage) modal is open; null = closed.
  const [invoiceEvent, setInvoiceEvent] = React.useState<DumpEvent | null>(
    null
  );
  // The dump receipt whose "Reassign" (change linked order) modal is open; null = closed.
  const [reassignEvent, setReassignEvent] = React.useState<DumpEvent | null>(
    null
  );
  // Rows with an in-flight confirm, so the button can disable itself and show
  // progress (prevents a double-press firing the PATCH twice).
  const [confirmingIds, setConfirmingIds] = React.useState<string[]>([]);

  // Drivers + dump yard templates populate the filter dropdowns. Driver value =
  // userId (= dump_events.driver_id); dump yard value = template id (the facility).
  React.useEffect(() => {
    dispatch(getDriversV2());
    dispatch(getDumpYardTemplates());
  }, [dispatch]);

  // Status / date / driver filter server-side; offset drives Prev/Next. Shared
  // so a save in the edit modal can re-pull the current page.
  const refetch = React.useCallback(() => {
    dispatch(
      getDumpEvents({ filters, pagination: { offset, count: PAGE_SIZE } })
    );
  }, [dispatch, filters, offset]);

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  // When in range mode, (re)fetch the summary whenever filters change so the
  // total bar stays in sync with the active filter set.
  React.useEffect(() => {
    if (mode !== "range") return;
    dispatch(getDumpEventsSummary({ filters }));
  }, [dispatch, filters, mode]);

  // One-click confirm: mark a receipt confirmed without opening the editor
  // (price/weight unchanged). Refreshes the page afterward.
  const confirmEvent = React.useCallback(
    async (id: string) => {
      setConfirmingIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
      try {
        await dispatch(
          updateDumpEvent({ id, update: { confirm: true } })
        ).unwrap();
      } catch {
        // error surfaced via the updateDumpEvent APIResult; keep the row as-is
      } finally {
        setConfirmingIds((ids) => ids.filter((x) => x !== id));
      }
      refetch();
    },
    [dispatch, refetch]
  );

  // Any server-side filter change returns to the first page.
  const applyFilters = React.useCallback((next: DumpEventFilters) => {
    setOffset(0);
    setFilters(next);
  }, []);

  // Navigate the date one day at a time (or jump via the calendar). Sets the
  // day's start/end as the server-side date range; preserves other filters.
  const goToDay = React.useCallback(
    (m: moment.Moment) => {
      const day = m.clone().startOf("day");
      setDayMoment(day);
      applyFilters({
        ...filters,
        startDate: day.toDate(),
        endDate: day.clone().endOf("day").toDate(),
      });
    },
    [applyFilters, filters]
  );

  // Switch to range mode: apply current rangeStart/rangeEnd as the date filter.
  // The summary fetch is driven by the [filters, mode] effect below — no need to
  // dispatch it here (doing so would double-fetch on the initial toggle).
  const switchToRange = React.useCallback(() => {
    const next: DumpEventFilters = {
      ...filters,
      startDate: rangeStart.clone().startOf("day").toDate(),
      endDate: rangeEnd.clone().endOf("day").toDate(),
    };
    setMode("range");
    applyFilters(next);
  }, [filters, rangeStart, rangeEnd, applyFilters]);

  // Switch back to day mode: restore the single-day filter; no total bar.
  const switchToDay = React.useCallback(() => {
    setMode("day");
    goToDay(dayMoment);
  }, [goToDay, dayMoment]);

  // Called when a range date input changes. Updates the relevant moment and
  // builds the next filters; applyFilters drives the list refetch and the
  // [filters, mode] effect below refetches the summary.
  const applyRangeChange = React.useCallback(
    (newStart: moment.Moment, newEnd: moment.Moment) => {
      setRangeStart(newStart);
      setRangeEnd(newEnd);
      const next: DumpEventFilters = {
        ...filters,
        startDate: newStart.clone().startOf("day").toDate(),
        endDate: newEnd.clone().endOf("day").toDate(),
      };
      applyFilters(next);
    },
    [filters, applyFilters]
  );

  // Close the calendar popover on an outside click.
  React.useEffect(() => {
    if (!showDatePicker) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showDatePicker]);

  // A full page back means there is probably a next page.
  const pageIsFull = dumpEvents.length === PAGE_SIZE;

  // Confidence filters client-side (the backend has no confidence filter), so it
  // narrows within the current page.
  const data: DumpEvent[] = React.useMemo(
    () =>
      confBand === "all"
        ? dumpEvents
        : dumpEvents.filter(
            (e) => confidenceBand(e.extractionConfidence) === confBand
          ),
    [dumpEvents, confBand]
  );

  const columns: Column<DumpEvent>[] = React.useMemo(
    () => [
      {
        Header: "Date",
        accessor: "createdAt",
        Cell: (props: CellProps<DumpEvent, string>) => {
          const d = new Date(props.value);
          return <>{d.toLocaleDateString()}</>;
        },
      },
      {
        id: "order",
        Header: "Order",
        accessor: (row: DumpEvent) => row.orderName || row.orderId || "",
        Cell: (props: CellProps<DumpEvent, string>) => {
          const o = props.row.original;
          if (!o.orderId) return <>—</>;
          const addr = [o.orderAddress, o.orderCity]
            .filter(Boolean)
            .join(", ");
          const meta = [
            dumpsterSizeLabel(o.orderDumpsterSize),
            o.orderBasePrice ? `Base $${o.orderBasePrice}` : "",
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <Link to={`/orders/${o.orderId}`} className="order-cell">
              <span className="order-name">
                {o.orderName || `Order ${o.orderId}`}
              </span>
              {addr && <span className="order-address">{addr}</span>}
              {meta && <span className="order-meta">{meta}</span>}
            </Link>
          );
        },
      },
      {
        id: "driver",
        Header: "Driver",
        accessor: (row: DumpEvent) => row.driverName || "—",
      },
      {
        id: "dumpYard",
        Header: "Dump Yard",
        accessor: (row: DumpEvent) => row.dumpYardName || "—",
      },
      {
        id: "type",
        Header: "Type",
        accessor: (row: DumpEvent) => receiptTypeLabel(row.receiptSource),
      },
      {
        id: "paid",
        Header: "Paid",
        accessor: (row: DumpEvent) => formatMoney(row.paidAmount),
      },
      {
        id: "weight",
        Header: "Weight",
        accessor: (row: DumpEvent) => formatWeight(row.weight),
      },
      {
        id: "confidence",
        Header: "Confidence",
        accessor: "extractionConfidence",
        Cell: (props: CellProps<DumpEvent, number | null>) => {
          const band = confidenceBand(props.row.original.extractionConfidence);
          const label =
            props.row.original.extractionConfidence !== null
              ? `${Math.round(props.row.original.extractionConfidence * 100)}%`
              : "—";
          return (
            <span className={`confidence-pill confidence-${band}`}>{label}</span>
          );
        },
      },
      {
        id: "status",
        Header: "Status",
        accessor: "extractionStatus",
        Cell: (props: CellProps<DumpEvent>) => {
          const cls = statusPillClass(props.row.original.extractionStatus);
          return <span className={cls}>{props.row.original.extractionStatus}</span>;
        },
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: "id",
        Cell: (props: CellProps<DumpEvent, string>) => (
          <div className="receipt-actions">
            <button
              className="btn btn-sm btn-view"
              onClick={() => {
                setSelectedReceiptId(props.value);
                if (onView) onView(props.value);
              }}
            >
              View
            </button>
            <button
              className="btn btn-sm btn-edit"
              onClick={() => setSelectedEditId(props.value)}
            >
              Edit
            </button>
            <button
              className="btn btn-sm btn-reassign"
              title="Change which order this receipt is linked to"
              onClick={() => setReassignEvent(props.row.original)}
            >
              Reassign
            </button>
            {props.row.original.extractionStatus === "extracted" && (
              <button
                className="btn btn-sm btn-confirm"
                disabled={confirmingIds.includes(props.value)}
                onClick={() => confirmEvent(props.value)}
              >
                {confirmingIds.includes(props.value)
                  ? "Confirming…"
                  : "Confirm"}
              </button>
            )}
            {props.row.original.orderId && (
              <button
                className="btn btn-sm btn-invoice"
                title="Raise a weight-overage invoice from this receipt"
                onClick={() => setInvoiceEvent(props.row.original)}
              >
                Invoice
              </button>
            )}
          </div>
        ),
      },
    ],
    [onView, confirmEvent, confirmingIds]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<DumpEvent>(
      {
        columns,
        data,
      },
      useSortBy
    );

  if (getDumpEventsResult.loading && dumpEvents.length === 0) {
    return (
      <div className="dump-event-receipts-table loading">
        <div className="loading-text">Loading receipts...</div>
      </div>
    );
  }

  if (
    !getDumpEventsResult.loading &&
    getDumpEventsResult.error &&
    dumpEvents.length === 0
  ) {
    return (
      <div className="dump-event-receipts-table error">
        <div className="error-text">
          Failed to load receipts. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="dump-event-receipts-table">
      <ReceiptModal
        receiptId={selectedReceiptId}
        onClose={() => setSelectedReceiptId(null)}
      />
      <EditDumpEventModal
        event={dumpEvents.find((e) => e.id === selectedEditId) ?? null}
        onClose={() => setSelectedEditId(null)}
        onSaved={refetch}
      />
      <DumpReceiptOverageModal
        event={invoiceEvent}
        onClose={() => setInvoiceEvent(null)}
        onInvoiced={refetch}
      />
      <ReassignOrderModal
        event={reassignEvent}
        onClose={() => setReassignEvent(null)}
        onSaved={refetch}
      />
      <PendingOveragesCard onActioned={refetch} />
      <div className="receipts-filter-bar">
        <label className="filter-field">
          <span>Status</span>
          <select
            value={filters.status?.[0] ?? ""}
            onChange={(e) =>
              applyFilters({
                ...filters,
                status: e.target.value
                  ? [e.target.value as ExtractionStatus]
                  : undefined,
              })
            }
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="extracted">Extracted</option>
            <option value="confirmed">Confirmed</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label className="filter-field">
          <span>Driver</span>
          <select
            value={filters.driverId ?? ""}
            onChange={(e) =>
              applyFilters({
                ...filters,
                driverId: e.target.value || undefined,
              })
            }
          >
            <option value="">All</option>
            {drivers.map((d) => (
              <option key={d.userId} value={d.userId}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>Dump Yard</span>
          <select
            value={filters.dumpYardTemplateId ?? ""}
            onChange={(e) =>
              applyFilters({
                ...filters,
                dumpYardTemplateId: e.target.value || undefined,
              })
            }
          >
            <option value="">All</option>
            {dumpYardTemplates
              .filter((t) => t.isDumpYard)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </label>
        <label className="filter-field">
          <span>Confidence</span>
          <select
            value={confBand}
            onChange={(e) =>
              setConfBand(e.target.value as "all" | "high" | "med" | "low")
            }
          >
            <option value="all">All</option>
            <option value="high">High (≥80%)</option>
            <option value="med">Med (50–79%)</option>
            <option value="low">Low (&lt;50%)</option>
          </select>
        </label>
        <div className="filter-field">
          <div className="receipts-date-nav">
            {/* Day | Range segmented toggle */}
            <div className="date-mode-toggle">
              <button
                type="button"
                className={`date-mode-btn${mode === "day" ? " date-mode-btn--active" : ""}`}
                onClick={switchToDay}
              >
                Day
              </button>
              <button
                type="button"
                className={`date-mode-btn${mode === "range" ? " date-mode-btn--active" : ""}`}
                onClick={switchToRange}
              >
                Range
              </button>
            </div>
            {mode === "day" ? (
              <>
                <button
                  type="button"
                  className="date-nav-arrow"
                  aria-label="Previous day"
                  onClick={() => goToDay(dayMoment.clone().add(-1, "day"))}
                >
                  <BsChevronLeft />
                </button>
                <div className="date-nav-anchor" ref={datePickerRef}>
                  <button
                    type="button"
                    className="date-nav-text"
                    onClick={() => setShowDatePicker((p) => !p)}
                  >
                    {dayMoment.format("ddd, Do MMM YYYY")}
                  </button>
                  {showDatePicker && (
                    <div className="date-nav-popover">
                      <DateCalendar
                        value={dayMoment}
                        onChange={(m) => {
                          if (m) {
                            goToDay(m);
                            setShowDatePicker(false);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="date-nav-arrow"
                  aria-label="Next day"
                  onClick={() => goToDay(dayMoment.clone().add(1, "day"))}
                >
                  <BsChevronRight />
                </button>
              </>
            ) : (
              <div className="date-range-inputs">
                <label className="date-range-label" htmlFor="range-from">
                  From
                  <input
                    id="range-from"
                    type="date"
                    value={rangeStart.format("YYYY-MM-DD")}
                    onChange={(e) => {
                      const m = moment(e.target.value, "YYYY-MM-DD", true);
                      if (m.isValid()) applyRangeChange(m, rangeEnd);
                    }}
                  />
                </label>
                <label className="date-range-label" htmlFor="range-to">
                  To
                  <input
                    id="range-to"
                    type="date"
                    value={rangeEnd.format("YYYY-MM-DD")}
                    onChange={(e) => {
                      const m = moment(e.target.value, "YYYY-MM-DD", true);
                      if (m.isValid()) applyRangeChange(rangeStart, m);
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      {mode === "range" && (
        <RangeTotalBar summary={summary} loading={summaryResult.loading} />
      )}
      <div className="table-container">
        <div className="table-scroll-wrapper">
          <table {...getTableProps()} className="receipts-table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td className="empty-row" colSpan={columns.length}>
                    No dump receipts found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="receipts-pagination">
        <button
          className="btn btn-sm"
          disabled={offset === 0}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Prev
        </button>
        <span className="page-indicator">Page {offset / PAGE_SIZE + 1}</span>
        <button
          className="btn btn-sm"
          disabled={!pageIsFull}
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
